import { OcrConfig } from './ocr-types'

const CONFIG_CACHE_KEY_PREFIX = 'opakor-ocr-config-'

interface RawOcrConfig {
  IMG_H: number
  IMG_W: number
  ENC_DIM: number
  ENC_LAYERS: number
  ENC_HEADS: number
  ENC_FF: number
  DEC_DIM: number
  DEC_LAYERS: number
  DEC_HEADS: number
  DEC_FF: number
  ctc_classes: number
  vocab_size: number
  ctc_offset: number
  blank_id: number
  unk_token: string
}

function normalizeConfig(raw: RawOcrConfig): OcrConfig {
  return {
    imgH: raw.IMG_H,
    imgW: raw.IMG_W,
    encDim: raw.ENC_DIM,
    encLayers: raw.ENC_LAYERS,
    encHeads: raw.ENC_HEADS,
    encFf: raw.ENC_FF,
    decDim: raw.DEC_DIM,
    decLayers: raw.DEC_LAYERS,
    decHeads: raw.DEC_HEADS,
    decFf: raw.DEC_FF,
    ctcClasses: raw.ctc_classes,
    vocabSize: raw.vocab_size,
    ctcOffset: raw.ctc_offset,
    blankId: raw.blank_id,
    unkToken: raw.unk_token,
  }
}

async function openCache(): Promise<Cache> {
  return caches.open('opakor-ocr-models')
}

async function fetchWithCache(cache: Cache, url: string, init?: RequestInit): Promise<Response> {
  const cached = await cache.match(url)
  if (cached) {
    return cached
  }
  const response = await fetch(url, init)
  if (response.ok) {
    await cache.put(url, response.clone())
    return response
  }
  const msg = await response.text().catch(() => 'Failed')
  throw new Error(msg || `HTTP ${response.status}`)
}

function isValidConfig(config: unknown): config is OcrConfig {
  return typeof config === 'object' && config !== null &&
    typeof (config as OcrConfig).imgH === 'number' &&
    typeof (config as OcrConfig).imgW === 'number'
}

export async function downloadOcrConfig(baseUrl: string): Promise<OcrConfig> {
  const cacheKey = `${CONFIG_CACHE_KEY_PREFIX}${baseUrl}`

  try {
    const stored = localStorage.getItem(cacheKey)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (isValidConfig(parsed)) {
        return parsed
      }
      localStorage.removeItem(cacheKey)
    }
  } catch {
    // Continue to fetch
  }

  const cache = await openCache()
  const url = `${baseUrl}/ocr_config.json`
  const response = await fetchWithCache(cache, url)
  const raw = (await response.json()) as RawOcrConfig
  const config = normalizeConfig(raw)

  try {
    localStorage.setItem(cacheKey, JSON.stringify(config))
  } catch {
    // localStorage may be full
  }

  return config
}

export async function downloadIdToToken(baseUrl: string): Promise<Record<number, string>> {
  const cache = await openCache()
  const url = `${baseUrl}/id_to_token.json`
  const response = await fetchWithCache(cache, url)
  return (await response.json()) as Record<number, string>
}

export async function downloadModelBuffer(baseUrl: string, modelName: string): Promise<ArrayBuffer> {
  const cache = await openCache()
  const url = `${baseUrl}/${modelName}`
  const response = await fetchWithCache(cache, url)
  return response.arrayBuffer()
}
